import React, { useState } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────
// 계약서 일괄 등록
//  - 여러 파일을 한 번에 올려 분석·저장까지 순차 처리
//  - 서버가 파일을 하나씩 처리하므로 건수가 많으면 시간이 걸린다
//  - 실패 파일은 서버의 uploads/contracts/failed/ 로 보관된다
// ─────────────────────────────────────────────

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function ContractBatchUpload() {
  const [files, setFiles] = useState([]);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [msg, setMsg] = useState('');

  const pick = (e) => {
    setFiles(Array.from(e.target.files || []));
    setResults(null);
    setMsg('');
  };

  const run = async () => {
    if (files.length === 0) {
      setMsg('파일을 선택하세요');
      return;
    }
    // [CT_BATCH_WARN_V1] 일괄등록은 파일 1개 = 계약 1건으로 처리됨.
    //   2장 이상 계약서(특약·공유자가 다음 장으로 넘어간 것)를 넣으면 각 장이 별도 계약으로 저장되므로 경고
    if (!window.confirm(`⚠️ 1장짜리 계약서만 일괄등록하세요.\n\n특약·공유자 등이 다음 장으로 넘어간 2장 이상 계약서는\n각 장이 따로 저장됩니다 → [➕ 새 계약서 등록]에서 한 건씩 등록하세요.\n\n${files.length}개 파일을 분석하고 저장합니다. (건당 수 초)\n진행할까요?`)) return;

    setRunning(true);
    setMsg(`처리 중... (${files.length}건, 잠시 기다려주세요)`);
    setResults(null);

    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));

    try {
      const res = await axios.post('/api/contracts/upload-batch', fd, {
        ...auth(),
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
        timeout: 30 * 60 * 1000,   // 건수가 많을 수 있어 넉넉히
      });
      if (res.data.success) {
        setResults(res.data.results);
        setMsg(res.data.message);
      } else {
        setMsg('실패: ' + res.data.message);
      }
    } catch (e) {
      setMsg('오류: ' + (e.response?.data?.message || e.message));
    } finally {
      setRunning(false);
    }
  };

  const okList = (results || []).filter((r) => r.ok);
  const failList = (results || []).filter((r) => !r.ok);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '20px', margin: 0 }}>📚 계약서 일괄 등록</h1>
        <button
          onClick={() => (window.location.href = '/contracts/list')}
          style={{
            backgroundColor: '#757575', color: 'white', border: 'none',
            padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
          }}
        >
          📋 목록으로
        </button>
      </div>

      {/* 파일 선택 */}
      <div style={{
        border: '2px dashed #2196F3', borderRadius: '8px',
        padding: '25px', textAlign: 'center', marginBottom: '15px',
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
          계약서 파일을 여러 개 선택하세요
        </div>
        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt"
          onChange={pick}
          disabled={running}
        />
        {files.length > 0 && (
          <div style={{ fontSize: '13px', color: '#2563eb', marginTop: '10px' }}>
            선택된 파일: {files.length}개
          </div>
        )}
      </div>

      <button
        onClick={run}
        disabled={running || files.length === 0}
        style={{
          width: '100%', backgroundColor: running || files.length === 0 ? '#ccc' : '#4CAF50',
          color: 'white', border: 'none', padding: '12px', borderRadius: '4px',
          cursor: running || files.length === 0 ? 'not-allowed' : 'pointer',
          fontSize: '15px', fontWeight: 'bold',
        }}
      >
        {running ? '⏳ 처리 중...' : '🚀 분석 후 일괄 저장'}
      </button>

      {msg && (
        <div style={{
          marginTop: '12px', padding: '10px', borderRadius: '4px',
          backgroundColor: '#f1f5f9', fontSize: '13px', textAlign: 'center',
        }}>
          {msg}
        </div>
      )}

      {/* ─── 결과 요약 ─── */}
      {results && (
        <div style={{ marginTop: '20px' }}>
          {/* 성공 */}
          {okList.length > 0 && (
            <>
              <h3 style={{ fontSize: '15px' }}>✅ 저장 완료 ({okList.length}건)</h3>
              {okList.map((r, i) => (
                <div
                  key={i}
                  onClick={() => window.open(`/contracts/${r.contractId}`, '_blank')}
                  style={{
                    padding: '10px', marginBottom: '6px', borderRadius: '4px',
                    backgroundColor: r.warns?.length ? '#fffbeb' : '#f0fdf4',
                    borderLeft: `4px solid ${r.warns?.length ? '#f59e0b' : '#4CAF50'}`,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
                    {r.summary?.type} · {r.summary?.address}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
                    계약일 {r.summary?.date || '-'} · 당사자 {r.summary?.parties}명
                    {' · '}ID {r.contractId}
                  </div>
                  {r.warns?.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#b45309', marginTop: '4px' }}>
                      ⚠️ 확인 필요: {r.warns.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* 실패 */}
          {failList.length > 0 && (
            <>
              <h3 style={{ fontSize: '15px', marginTop: '20px' }}>❌ 실패 ({failList.length}건)</h3>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                실패한 파일은 서버의 failed 폴더에 보관됩니다. 직접 등록하시거나 원본을 확인하세요.
              </div>
              {failList.map((r, i) => (
                <div
                  key={i}
                  style={{
                    padding: '10px', marginBottom: '6px', borderRadius: '4px',
                    backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{r.file}</div>
                  <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '3px' }}>
                    {r.reason}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
